import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { Price, PriceDetail, PriceFormData, PriceScheduleFormItem, ApiDayKey } from '../models/price.model';
import { PaginatedResponse } from './base-crud.service';

interface ApiPaginatedResponse<T> {
  data: T[];
  current_page: number;
  page_size: number;
  total_elements: number;
  total_pages: number;
}

interface ApiDataWrapper<T> {
  data: T;
}

/** Configuraciones de precio (`catalogs-api/v1/prices`): CRUD y duplicado de una configuración existente. */
@Injectable({ providedIn: 'root' })
export class PricesService {

  private http = inject(HttpClient);
  private baseUrl = `${environment.apiBaseUrl}/${environment.apiPrefixes.catalogs}/${environment.apiVersions.catalogs}/prices`;

  // ─── CRUD principal ─────────────────────────────────────────────────────────

  getAll(
    page = 1,
    perPage = 10,
    filters: Record<string, any> = {},
    sortField?: string,
    sortOrder?: number
  ): Observable<PaginatedResponse<Price>> {
    let params = new HttpParams()
      .set('page', (page - 1).toString())
      .set('size', perPage.toString());

    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params = params.set(key, value.toString());
      }
    });

    if (sortField) {
      const dir = sortOrder === 1 ? 'asc' : 'desc';
      params = params.set('sort', `${this.snakeToCamel(sortField)}:${dir}`);
    }

    return this.http.get<ApiPaginatedResponse<Price>>(this.baseUrl, { params }).pipe(
      map(res => ({
        data: res.data.map(item => this.enrichPrice(item)),
        meta: {
          page: res.current_page + 1,
          per_page: res.page_size,
          total_items: res.total_elements,
          total_pages: res.total_pages
        }
      }))
    );
  }

  getById(id: number): Observable<PriceDetail> {
    return this.http
      .get<ApiDataWrapper<PriceDetail>>(`${this.baseUrl}/${id}`)
      .pipe(map(res => res.data));
  }

  create(data: PriceFormData): Observable<Price> {
    return this.http
      .post<ApiDataWrapper<Price>>(this.baseUrl, data)
      .pipe(map(res => res.data));
  }

  update(id: number, data: PriceFormData): Observable<Price> {
    return this.http
      .put<ApiDataWrapper<Price>>(`${this.baseUrl}/${id}`, data)
      .pipe(map(res => res.data));
  }

  delete(id: number): Observable<boolean> {
    return this.http
      .delete<void>(`${this.baseUrl}/${id}`)
      .pipe(map(() => true));
  }

  // ─── Duplicar ───────────────────────────────────────────────────────────────

  duplicate(id: number): Observable<Price> {
    return this.getById(id).pipe(
      switchMap(detail => {
        const schedule: PriceScheduleFormItem[] = detail.schedule.map(s => ({
          start_time: s.start_time,
          end_time: s.end_time,
          prices: s.prices as Record<ApiDayKey, number>
        }));

        const formData: PriceFormData = {
          name: `${detail.name} (Copia)`,
          timezone_id: detail.timezone_id,
          commercial_zone_id: detail.commercial_zone_id,
          commercial_condition_id: detail.commercial_condition_id,
          validity_start_date: detail.validity_start_date,
          validity_end_date: detail.validity_end_date,
          tax_percentage: detail.tax_percentage,
          is_active: detail.is_active,
          confirm_gaps: false,
          schedule
        };

        return this.create(formData);
      })
    );
  }

  // ─── Helpers privados ───────────────────────────────────────────────────────

  private enrichPrice(item: Price): Price {
    return {
      ...item,
      timezone_name: item.timezone?.name ?? '',
      commercial_zone_name: item.commercial_zone?.name ?? '',
      commercial_condition_name: item.commercial_condition?.name ?? '',
      validity_display: this.formatValidityDisplay(item.validity_start_date, item.validity_end_date)
    };
  }

  private formatValidityDisplay(start: string, end: string | null): string {
    // Extraer solo la parte de fecha (YYYY-MM-DD) para evitar conversión de zona horaria
    const fmt = (s: string) => {
      const [year, month, day] = s.split('T')[0].split('-').map(Number);
      return new Date(year, month - 1, day).toLocaleDateString('es-MX', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });
    };
    return `${fmt(start)} - ${end ? fmt(end) : 'Indefinido'}`;
  }

  private snakeToCamel(str: string): string {
    return str.replace(/_([a-z])/g, (_, l) => l.toUpperCase());
  }
}

